import React, { useState, useEffect, useRef } from "react";
import { connect } from "react-redux";
import { createStore } from 'devextreme-aspnet-data-nojquery';
import axios from "axios";
import data from "../../../../assets/apiRoutes.json";
import { Popup, ToolbarItem } from 'devextreme-react/popup';
import { alert } from 'devextreme/ui/dialog';
import { SelectBox } from 'devextreme-react/select-box';
import { TextBox } from 'devextreme-react/text-box';
import {
  Column,
  DataGrid,
  GroupPanel,
  Grouping,
  Selection,
  FilterPanel,
  Sorting,
  Paging,
  Pager,
  LoadPanel,
  ColumnHeaderFilter,
  Toolbar as GridToolbar,
  Item as GridToolbarItem,
} from "devextreme-react/data-grid";

const reasonCodeSelectBox = React.createRef();
const commentTextBox = React.createRef();

/**
 * Unified popup for placing a lot on hold or releasing it from hold.
 * Controlled by the `mode` prop: "on" (default) or "off".
 *
 * Business rule (applies to both modes): the entire lot must be placed on hold /
 * released together. Partial holds are not allowed. The server also re-validates whole-lot
 * coverage on submit.
 */
const LotHoldPopUp = (props) => {
  const isOffHold = props.mode === "off";

  const dataGrid = useRef(null);
  const [totalQty, setTotalQty] = useState(0.0);
  const [popupState, setPopUpState] = useState(props.visibility);
  const [noDataText, setNoDataText] = useState("Loading Data ...");
  const [locationData, setLocationData] = useState([]);
  var accessToken = props.oidc.user.access_token;
  const dataLotGrid = [{
    'ItemCode': props.itemObj.itemCode,
    'LotCode': props.itemObj.lotCode,
    'Description-1': props.itemObj.lotDescription1,
    'Description-2': props.itemObj.lotDescription2,
    'Status': props.itemObj.lotStatus
  }];

  const popupTitle = isOffHold ? "Lot Off Hold" : "Lot On Hold";
  const submitText = isOffHold ? "Put Off Hold" : "Put On Hold";
  const qtyCaption = isOffHold ? "Off Hold Qty" : "On Hold Qty";
  const submitRoute = isOffHold ? data.Lot.PutOffHold : data.Lot.PutOnHold;
  const emptyDataText = isOffHold
    ? "No on-hold inventory you can release for this lot."
    : "No Data";

  const reasonCodesDataSource = createStore({
    loadUrl: `${window._env_.REACT_APP_APIS}/${data.Lot.GetReasonCodes}`,
    onBeforeSend: function (method, ajaxOptions) {
      ajaxOptions.headers = {
        Authorization: `Bearer ${accessToken}`
      };
    }
  });

  const hideInfo = () => {
    setPopUpState(false);
    props.parentCallback(false);
  };

  const submitHold = () => {
    if (locationData == null || locationData.length === 0) {
      alert(
        isOffHold
          ? "There is no on-hold inventory to release for this lot."
          : "There are no locations available to place on hold for this lot.",
        "Error"
      );
      return;
    }

    var reasoncode = reasonCodeSelectBox.current.instance().option("value");
    if (reasoncode == null || reasoncode === undefined || reasoncode === "") {
      alert("Please provide a reason code", "Error");
      return;
    }
    var comment = commentTextBox.current.instance().option("value");
    var payload = new Object();
    payload.ReasonCode = reasoncode;
    payload.Comment = comment;
    // Whole-lot rule: always submit every loaded location, not a user selection.
    payload.Locations = locationData;

    axios
      .post(`${window._env_.REACT_APP_APIS}/${submitRoute}`, payload,
        {
          headers: {
            "Content-Type": "application/json",
            access_token: accessToken,
            Authorization: `Bearer ${accessToken}`,
          },
        })
      .then((res) => {
        if (res.data.isSuccess) {
          setPopUpState(false);
          props.parentCallback(true);
        }
        else {
          alert(res.data.errorMessage, "Error");
        }
      })
      .catch((err) => {
        alert(err, "Error");
      });
  };

  useEffect(() => {
    loadInventoryLocationData();
  }, []);

  const loadInventoryLocationData = () => {
    setNoDataText("Loading Data ...");
    axios
      .post(`${window._env_.REACT_APP_APIS}/${data.Lot.GetLotLocations}`, 
        {
          lotRowid: props.itemObj.lotRowid,
          AvailableGreaterThanZero: isOffHold ? false : true
        },
        {
          headers: {
            "Content-Type": "application/json",
            access_token: accessToken,
            Authorization: `Bearer ${accessToken}`,
          },
        })
      .then((res) => {
        setLocationData(res.data);
        computeTotal(res.data);
        if (res.data.length === 0) {
          setNoDataText(emptyDataText);
        } 
      });
  };

  const computeTotal = (rows) => {
    var total = 0.0;
    if (rows != null) {
      for (var i = 0; i < rows.length; i++) {
        if (rows[i].onhold != null) {
          total = total + rows[i].onhold;
        }
      }
    }
    setTotalQty(total);
  };

  return (
    <Popup
      visible={popupState}
      onHiding={hideInfo}
      dragEnabled={false}
      hideOnOutsideClick={true}
      showCloseButton={true}
      showTitle={true}
      title={popupTitle}
      container="#root"
      className="custom-popup"
    >
      <ToolbarItem
        widget="dxButton"
        toolbar="top"
        location="after"
        options={{
          text: submitText,
          stylingMode: "contained",
          icon: "save",
          onClick: () => submitHold()
        }}
      />

      {/* Lot Grid */}
      <DataGrid
        onExporting={(e) => {
          if (window.handleDevExtremeDataGridExport) {
            window.handleDevExtremeDataGridExport(e);
          }
        }}
        dataSource={dataLotGrid}
        showBorders={true}
      >
        <Column dataField="ItemCode" caption="Item Code"></Column>
        <Column dataField="LotCode" caption="Lot Code"></Column>
        <Column dataField="Description-1" caption="Lot Description-1"></Column>
        <Column dataField="Description-2" caption="Lot Description-2"></Column>
        <Column dataField="Status" caption="Lot Status"></Column>
      </DataGrid>
      <div className="row">
        <SelectBox
          style={{ marginTop: "10px", marginBottom: "10px" }}
          ref={reasonCodeSelectBox}
          placeholder="Reason Code"
          stylingMode='outlined'
          labelMode="static"
          dataSource={reasonCodesDataSource}
          searchEnabled={true}
          width={300}
        />
        <TextBox
          style={{ marginLeft: "20px", marginTop: "10px", marginBottom: "10px" }}
          stylingMode='outlined'
          labelMode="static"
          width={582}
          ref={commentTextBox}
          maxLength={1000}
          placeholder="Comment"
        />
      </div>
      
      <div className="row">
        <DataGrid
          onExporting={(e) => {
            if (window.handleDevExtremeDataGridExport) {
              window.handleDevExtremeDataGridExport(e);
            }
          }}
          ref={dataGrid}
          noDataText={noDataText}
          dataSource={locationData}
          keyExpr="id"
          showBorders={true}
          className="popupOnHoldGrid-height"
          hoverStateEnabled={true}
          allowColumnResizing={true}
          allowColumnReordering={true}
        >
          <ColumnHeaderFilter />
          <Sorting enabled={false} />
          <LoadPanel enabled={true} />
          <Paging enabled={false} />
          <Pager visible={false} />
          <FilterPanel visible={false} />
          <GroupPanel visible={false} />
          <Grouping autoExpandAll={false} />
          {props.itemObj.itemType === "S" && (
            <Column dataField="sublotCode" caption="SubLot" allowEditing={false}></Column>
          )}
          <Column dataField="locationCode" caption="Location" allowEditing={false}></Column>
          <Column dataField="onhold" caption={qtyCaption} allowEditing={false}></Column>
          <GridToolbar>
              <GridToolbarItem location="after">
                <div><b>Total: {totalQty}</b></div>
              </GridToolbarItem>
          </GridToolbar>
        </DataGrid>
      </div>
    </Popup>
  );
};

function mapStateToProps(state) {
  return {
    ReceiptReducer: state.ReceiptReducer,
    oidc: state.oidc,
  };
}
export default connect(mapStateToProps)(LotHoldPopUp);
